import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Shield, Search, Download, X, Loader2,
  RefreshCw, ExternalLink, ChevronRight,
  EyeOff,
  TriangleAlert,
  ChevronLeft
} from 'lucide-react';
import authService from '../services/AuthService';
import UngateCrawler from './Ungatecrawler';
import UngateCrawlerV2 from './UngateCrawlerV2';
import { SK_HISTORICAL_LEADS } from './Ungatecrawler';

const API_BASE = 'https://server.multisellerpro.com/api';

const isUsMarketplaceUser = (user) => (user?.amazon_marketplace || '').toUpperCase() === 'US';

const AMAZON_CATEGORY_WHITELIST = new Set([
  "Alexa Skills", "Amazon Autos", "Amazon Devices & Accessories", "Amazon Luxury",
  "Appliances", "Apps & Games", "Arts, Crafts & Sewing", "Audible Books & Originals",
  "Automotive", "Baby Products", "Beauty & Personal Care", "Books", "CDs & Vinyl",
  "Cell Phones & Accessories", "Clothing, Shoes & Jewelry", "Collectibles & Fine Art",
  "Digital Music", "Electronics", "Everything Else", "Gift Cards",
  "Grocery & Gourmet Food", "Handmade Products", "Health & Household",
  "Home & Kitchen", "Industrial & Scientific", "Kindle Store",
  "Magazine Subscriptions", "Movies & TV", "Musical Instruments", "Office Products",
  "Patio, Lawn & Garden", "Pet Supplies", "Software", "Sports & Outdoors",
  "Tools & Home Improvement", "Toys & Games", "Video Games", "Video Shorts",
]);

const REFRESH_ENDPOINTS = {
  live_scan: (id) => `${API_BASE}/live-scan/deals/${id}/refresh-gated`,
  storefront: (id) => `${API_BASE}/storefront/deals/${id}/refresh-gated`,
  wholesale: (id) => `${API_BASE}/wholesale/deals/${id}/refresh-gated`,
  reverse_scan: (id) => `${API_BASE}/reverse-scan/deals/${id}/refresh-gated`,
};

const GATED_STATUS_STYLE = {
  ungated: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Ungated' },
  gated: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Gated' },
  approval: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'Approval Needed' },
  needs_login: { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', label: 'Needs Login' },
  error: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Error' },
  unknown: { bg: 'bg-gray-50', text: 'text-gray-400', border: 'border-gray-200', label: 'Unknown' },
};

// ── Brand Products Modal ──────────────────────────────────────────────────────
const BrandProductsModal = ({ brand, onClose, onEstimateUpdate, initialEstimatePct = null }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({}); // { [deal_id]: true/false }
  const [keepaLoading, setKeepaLoading] = useState(false);
  const [keepaProgress, setKeepaProgress] = useState({ checked: 0, total: 0 });
  const [keepaASINFetching, setKeepaASINFetching] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [hideParents, setHideParents] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const enrichQueueRef = useRef([]);
  const enrichTimerRef = useRef(null);
  const cancelRef = useRef(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const [estimatePct, setEstimatePct] = useState(initialEstimatePct);
  const [estimateUpdatedThisSession, setEstimateUpdatedThisSession] = useState(false);
  const estimateTally = useRef({ checked: 0, ungated: 0, lastPushedAt: 0 });
  const estimateTotalExpected = useRef(0); // total asins we expect to check this session (seed + keepa batch)

  const flushEnrichQueue = async () => {
    if (enrichQueueRef.current.length === 0) return;
    const batch = enrichQueueRef.current.splice(0, 25);
    try {
      const res = await authService.authenticatedFetch(`${API_BASE}/amazon/quick-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asins: batch }),
      });
      const data = await res.json();
      const products = data.products || {};
      setProducts(prev => {
        const existingAsins = new Set(prev.map(p => p.asin));
        const newChildRows = [];

        const updated = prev.map(p => {
          const d = products[p.asin];
          if (!d) return p;
          const isParent = !!d.variation_asins?.length && !d.parent_asin;
          if (isParent && d.variation_asins?.length) {
            d.variation_asins.forEach(childAsin => {
              if (!existingAsins.has(childAsin)) {
                existingAsins.add(childAsin);
                newChildRows.push({
                  asin: childAsin,
                  deal_id: null,
                  source: null,
                  title: '',
                  image: '',
                  price: null,
                  gated_status: 'checking',
                  isKeepa: true,
                  isChildOf: p.asin,
                });
              }
            });
          }
          return {
            ...p,
            title: p.title || d.title || '',
            image: p.image || d.image || '',
            price: p.price || d.price || null,
            sales_rank: p.sales_rank || d.sales_rank || 0,
            monthly_sold: p.monthly_sold || d.monthly_sold || -1,
            sales_rank_drops_30: d.sales_rank_drops_90 ? Math.round(d.sales_rank_drops_90 / 3) : (d.sales_rank_drops_30 || 0),
            total_offers: p.total_offers || d.total_offers || 0,
            isParentAsin: isParent,
            childAsins: isParent ? (d.variation_asins || []) : p.childAsins,
          };
        });

        return [...updated, ...newChildRows];
      });

      // Kick off gating check for any new child rows
      setProducts(prev => {
        const children = prev.filter(p => p.isChildOf && p.gated_status === 'checking');
        if (children.length > 0) {
          estimateTotalExpected.current += children.length;
          checkGatedBulk(children.map(p => p.asin));
        }
        return prev;
      });
    } catch (e) {
      console.error('quick-batch enrich failed:', e);
    }
    // If more remain, keep draining
    if (enrichQueueRef.current.length > 0) {
      enrichTimerRef.current = setTimeout(flushEnrichQueue, 500);
    }
  };

  const scheduleEnrich = (asin) => {
    enrichQueueRef.current.push(asin);
    clearTimeout(enrichTimerRef.current);
    // Wait 5s to batch up ungated results, or flush immediately at 25
    if (enrichQueueRef.current.length >= 25) {
      flushEnrichQueue();
    } else {
      clearTimeout(enrichTimerRef.current);
      enrichTimerRef.current = setTimeout(flushEnrichQueue, 5000);
    }
  };

  const pushEstimate = async (force = false) => {
    const { checked, ungated } = estimateTally.current;
    if (checked === 0) return;

    const reachedHundred = checked >= 100;
    const reachedFullSet = estimateTotalExpected.current > 0 && checked >= estimateTotalExpected.current;
    if (!force && !reachedHundred && !reachedFullSet) return; // not a valid checkpoint yet

    const pct = Math.round((ungated / checked) * 1000) / 10;
    setEstimatePct(pct);
    setEstimateUpdatedThisSession(true);
    estimateTally.current.lastPushedAt = checked;
    try {
      const res = await authService.authenticatedFetch(`${API_BASE}/user/ungated-brands/v2/set-estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand, checked_count: checked, ungated_count: ungated }),
      });
      const data = await res.json();
      onEstimateUpdate?.(brand, pct, data.ungate_estimate_updated_at);
    } catch { /* best-effort */ }
  };

  const checkGatedBulk = async (asins) => {
    setKeepaLoading(true);
    setKeepaProgress(p => ({ checked: p.checked, total: p.total + asins.length }));
    const batchSize = 10;

    for (let i = 0; i < asins.length; i += batchSize) {
      if (cancelRef.current) break;
      const batch = asins.slice(i, i + batchSize);
      try {
        const res = await authService.authenticatedFetch(
          `${API_BASE}/a2a/check-gated-bulk`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ asins: batch, skip_cache: true })
          }
        );
        const data = await res.json();
        if (!data.results) continue;

        if (Object.values(data.results).some(status => status === 'needs_login')) {
          setNeedsLogin(true);
        }

        setProducts(prev =>
          prev
            .map(p => {
              const newStatus = data.results[p.asin];
              if (!newStatus) return p;
              if (newStatus === 'ungated' && (!p.title || !p.image)) {
                scheduleEnrich(p.asin);
              }
              return { ...p, gated_status: newStatus };
            })
            .filter(p => !p.isKeepa || ['ungated', 'approval', 'checking', 'gated'].includes(p.gated_status))
        );

        // Tally toward the brand's estimated-ungated %, pushed every 100 checked
        Object.values(data.results).forEach(s => {
          if (s === 'needs_login' || s === 'error') return;
          estimateTally.current.checked += 1;
          if (s === 'ungated') estimateTally.current.ungated += 1;
        });
        if (estimateTally.current.checked - estimateTally.current.lastPushedAt >= 100) {
          pushEstimate();
        }
      } catch (e) {
        console.error(e);
      }
      setKeepaProgress(p => ({ ...p, checked: p.checked + batch.length }));
    }

    // Remove any rows that never got a result
    setProducts(prev => prev.filter(p => !p.isKeepa || p.gated_status !== 'checking'));
    setKeepaLoading(false);
    // This call's full batch is done (whether that's 100+ or the brand's entire
    // available set) — this always counts as a valid "complete scan" checkpoint.
    pushEstimate(true);
  };

  const fetchKeepaAsins = async (existingProducts) => {
    setKeepaASINFetching(true);
    try {
      const res = await authService.authenticatedFetch(
        `${API_BASE}/user/ungated-brands/keepa-asins`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_name: brand })
        }
      );
      const data = await res.json();
      const keepaAsins = data.asins || [];

      // Remove ASINs we already have
      const existingAsins = new Set(existingProducts.map(p => p.asin));
      const newAsins = keepaAsins.filter(a => !existingAsins.has(a));

      if (newAsins.length === 0) return;

      // Append new rows with gated_status: 'checking'
      const newRows = newAsins.map(asin => ({
        asin,
        deal_id: null,
        source: null,
        title: '',
        image: '',
        price: null,
        gated_status: 'checking',
        isKeepa: true,
      }));

      setProducts(prev => [...prev, ...newRows]);
      setPage(1);
      estimateTotalExpected.current += newAsins.length;

      setKeepaASINFetching(false);

      // Now check gated in batches of 10
      checkGatedBulk(newAsins);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProducts = async () => {
    cancelRef.current = false;
    setLoading(true);
    try {
      const res = await authService.authenticatedFetch(
        `${API_BASE}/user/ungated-brands/products`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_name: brand })
        }
      );
      const data = await res.json();
      const existingProducts = data.products || [];
      setProducts(existingProducts);

      // Seed the tally with statuses we already know about (from deal tables),
      // so the FINAL estimate reflects everything known for this brand — but
      // don't reveal/push it yet, just fold it into the running count silently.
      // It surfaces once checkGatedBulk hits 100 checked or fully finishes.
      const KNOWN_STATUSES = new Set(['ungated', 'gated', 'approval']);
      existingProducts.forEach(p => {
        if (!KNOWN_STATUSES.has(p.gated_status)) return;
        estimateTally.current.checked += 1;
        if (p.gated_status === 'ungated') estimateTally.current.ungated += 1;
      });
      estimateTotalExpected.current += estimateTally.current.checked;

      fetchKeepaAsins(existingProducts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    return () => {
      cancelRef.current = true;
      clearTimeout(enrichTimerRef.current);
      pushEstimate(); // force=false: only saves if 100+ checked or the full expected set was reached
    };
  }, [brand]);

  const handleRefresh = async (product) => {
    const { deal_id, source } = product;
    const endpointFn = REFRESH_ENDPOINTS[source];
    if (!endpointFn) return;

    setRefreshing(r => ({ ...r, [deal_id]: true }));
    try {
      const res = await authService.authenticatedFetch(endpointFn(deal_id), { method: 'POST' });
      const data = await res.json();

      if (data.gated_status) {
        setProducts(prev =>
          prev.map(p =>
            p.deal_id === deal_id
              ? { ...p, gated_status: data.gated_status }
              : p
          )
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(r => ({ ...r, [deal_id]: false }));
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) { cancelRef.current = true; onClose(); }
  };

  const keepaUrl = `https://keepa.com/#!finder/${encodeURIComponent(JSON.stringify({
    f: { brand: { filterType: "autocomplete", filter: brand.toLowerCase() } },
    s: [{ colId: "SALES_current", sort: "asc" }, { colId: "monthlySold", sort: "desc" }],
    t: "g"
  }))}`;

  const paginated = [...products]
    .filter(p => !hideParents || !p.isParentAsin)
    .filter(p => !productSearch || p.asin.toLowerCase().includes(productSearch.toLowerCase()) || p.title.toLowerCase().includes(productSearch.toLowerCase()))
    .sort((a, b) => (({ ungated: 0, approval: 1, checking: 2, gated: 3 })[a.gated_status] ?? 4) - (({ ungated: 0, approval: 1, checking: 2, gated: 3 })[b.gated_status] ?? 4))
    .slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 top-12"
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-800 text-base flex items-center gap-2">
              Products for brand -{' '}
              <span className="text-emerald-600">{brand}</span>
              {estimatePct !== null && (
                <span className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                  {estimatePct}% Estimated Ungated Asins
                  {(keepaLoading || keepaASINFetching) && (
                    <Loader2 size={10} className="animate-spin" title="Still scanning — updates every 100 ASINs checked" />
                  )}
                </span>
              )}
            </h2>
            {!loading && (
              <p className="text-xs text-gray-400 mt-0.5">
                {products.length} product{products.length !== 1 ? 's' : ''} found ·
                click a row to open on Amazon · use Refresh to re-check gating
              </p>
            )}
            {!loading && (
              <div className="relative mt-1.5">
                <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300" />
                <input
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  placeholder="Search by name or ASIN…"
                  className="w-full pl-7 pr-6 py-1 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                {productSearch && (
                  <button onClick={() => setProductSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                    <X size={10} />
                  </button>
                )}
              </div>
            )}
            {!loading && products.some(p => p.isParentAsin) && (
              <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer text-orange-600 mt-0.5">
                <input
                  type="checkbox"
                  checked={hideParents}
                  onChange={e => setHideParents(e.target.checked)}
                  className="accent-orange-500"
                />
                Hide parent ASINs ({products.filter(p => p.isParentAsin).length})
              </label>
            )}
            {keepaLoading && (
              <span className="flex items-center gap-1 text-blue-500">
                <Loader2 size={11} className="animate-spin" />
                Finding all brand UNGATED ASINs for you...
                {keepaProgress.total > 0 && (
                  <span className="text-gray-400 font-normal">({keepaProgress.checked}/{keepaProgress.total} checked)</span>
                )}
              </span>
            )}
          </div>
          <button
            onClick={() => { cancelRef.current = true; onClose(); }}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-500 transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-16 flex flex-col items-center gap-3 text-gray-400">
              <Loader2 size={24} className="animate-spin" />
              <span className="text-sm">Loading products…</span>
            </div>
          ) : keepaASINFetching ? (
            <div className="py-16 flex flex-col items-center gap-4 text-gray-400">
              <Loader2 size={28} className="animate-spin text-emerald-500" />
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-600">Scanning for <span className="text-emerald-600">{brand}</span>…</p>
                <p className="text-xs text-gray-400 mt-1">Pulling ASINs, hang tight</p>
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">
              No products found for this brand.
              {needsLogin && (
                <div style={{ textAlign: "center" }}><br /><button
                  onClick={() => {
                    window.open(
                      `https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${encodeURIComponent(authService.credentials?.email || '')}`,
                      "_blank"
                    );
                  }}
                  style={{ border: "none", background: "transparent", cursor: "pointer", display: "flex", gap: "14px", margin: "auto", color: "red" }}
                >
                  You MUST link your Amazon account:
                  <img
                    src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_101x46.png"
                    style={{ height: 25 }}
                    alt="Link Amazon Account"
                  />
                </button></div>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-100 z-10">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 w-14">Image</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Product</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-24">Price</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 w-36">Status</th>
                  <th className="px-4 py-3 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((p, i) => {
                  const style = GATED_STATUS_STYLE[p.gated_status] || GATED_STATUS_STYLE.unknown;
                  const isRefreshing = !!refreshing[p.deal_id];

                  return (
                    <tr
                      key={p.deal_id || i}
                      className={`border-b border-gray-50 transition group ${p.isParentAsin ? 'bg-orange-50 hover:bg-orange-100' : p.isChildOf ? 'bg-gray-50/60 hover:bg-gray-100' : 'hover:bg-gray-50'}`}
                    >
                      {/* Image */}
                      <td className="px-5 py-3">
                        {p.image ? (
                          <img
                            src={p.image}
                            alt=""
                            className="w-10 h-10 object-contain rounded-md bg-gray-50"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center text-gray-300 text-[10px]">
                            N/A
                          </div>
                        )}
                      </td>

                      {/* Title + ASIN + open Amazon */}
                      <td className="px-4 py-3">
                        <a
                          href={`https://www.amazon${authService.getAmazonMarketplace()}/dp/${p.asin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-gray-800 hover:text-emerald-600 transition leading-snug flex items-start gap-1 group/link"
                          style={{ display: 'flex', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                          onClick={e => e.stopPropagation()}
                        >
                          {p.title || 'Click to open on Amazon'}
                          <ExternalLink size={11} className="flex-shrink-0 mt-0.5 opacity-0 group-hover/link:opacity-100 transition text-emerald-500" />
                        </a>
                        <p className="text-xs text-gray-400 mt-0.5 font-mono">{p.asin}</p>
                        {(p.sales_rank > 0 || p.monthly_sold > 0 || p.sales_rank_drops_30 > 0 || p.total_offers > 0) && (
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap text-[10px] text-gray-400">
                            {[
                              p.sales_rank > 0 && `#${p.sales_rank.toLocaleString()} bsr`,
                              p.monthly_sold > 0 && `${p.monthly_sold.toLocaleString()} sales/mo`,
                              p.sales_rank_drops_30 > 0 && `${p.sales_rank_drops_30} bsr drops/mo`,
                              p.total_offers > 0 && `${p.total_offers} offers`,
                            ].filter(Boolean).map((val, i, arr) => (
                              <React.Fragment key={i}>
                                <span>{val}</span>
                                {i < arr.length - 1 && <span className="text-gray-300">·</span>}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                        {p.isParentAsin && (
                          <div className="mt-1 space-y-0.5">
                            <div className="flex items-center gap-1 text-orange-600 text-[11px] font-semibold">
                              <TriangleAlert size={11} />
                              Parent ASIN - you may still be gated on child
                            </div>
                            {p.childAsins?.length > 0 && (
                              <>
                                <div className="text-[10px] text-gray-500">
                                  {p.childAsins.length} child{p.childAsins.length !== 1 ? 's' : ''} added to the list and being checked for you!
                                </div>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {p.childAsins.map(asin => (
                                    <span key={asin} className="font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                                      {asin}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {p.isChildOf && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            ↳ Child of <span className="font-mono">{p.isChildOf}</span>
                          </div>
                        )}
                      </td>

                      {/* Price */}
                      <td className="px-4 py-3 text-gray-700 font-semibold">
                        {p.price ? `$${Number(p.price).toFixed(2)}` : '—'}
                      </td>

                      {/* Gated status badge */}
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${style.bg} ${style.text} ${style.border}`}>
                          {style.label}
                        </span>
                      </td>

                      {/* Refresh button */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleRefresh(p)}
                          disabled={isRefreshing}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50 text-gray-600 text-xs font-semibold rounded-lg transition"
                          title="Re-check gating status on Amazon"
                        >
                          {isRefreshing
                            ? <Loader2 size={11} className="animate-spin" />
                            : <RefreshCw size={11} />}
                          {isRefreshing ? 'Checking…' : 'Refresh'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {products.length > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 py-3 border-t border-gray-100">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 disabled:opacity-40 rounded-lg transition"
              >
                ← Prev
              </button>
              <span className="text-xs text-gray-500">
                Page {page} of {Math.ceil(products.length / PAGE_SIZE)}
              </span>
              <button
                onClick={() => setPage(p => Math.min(Math.ceil(products.length / PAGE_SIZE), p + 1))}
                disabled={page === Math.ceil(products.length / PAGE_SIZE)}
                className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 disabled:opacity-40 rounded-lg transition"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-400 flex items-center gap-1.5">
          <Shield size={11} />
          Always verify gating status before purchasing inventory. Use the Refresh button to re-check live on Amazon.
          <a
            href={keepaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-1.5 px-4 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-semibold rounded-lg transition"
          >
            <ExternalLink size={12} />
            Show All Products for This Brand
          </a>
          <button
            disabled={keepaLoading}
            onClick={() => {
              const ungated = products.filter(p => p.gated_status === 'ungated');
              const csv = ['ASIN,Status',
                ...ungated.map(p => `${p.asin},${p.gated_status}`)
              ].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `ungated-${brand}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="flex items-center gap-1.5 px-4 py-1.5 ml-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition"
          >
            {keepaLoading
              ? <><Loader2 size={12} className="animate-spin" /> Scanning...</>
              : <><Download size={12} /> Export Ungated</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Widget ───────────────────────────────────────────────────────────────
const getBulkKeepaUrl = (brands) => {
  const now = new Date();
  const ym = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const params = {
    f: {
      brand: { filterType: 'autocomplete', filter: brands.join('###'), type: 'isOneOf' },
      srAvgMonth: { filterType: 'text', type: 'equals', filter: ym },
    },
    s: [{ colId: 'SALES_current', sort: 'asc' }, { colId: 'monthlySold', sort: 'desc' }],
    t: 'f',
  };
  return `https://keepa.com/#!finder/${encodeURIComponent(JSON.stringify(params))}`;
};

const KEEPA_LIMIT = 50;
const LS_AUTO_MARK_REVIEWED = 'uc_auto_mark_reviewed';
const BRAND_PAGE_SIZE = 300; // how many brand badges to render at a time
const LEADS_PAGE_SIZE = 100; // how many historical lead rows to render at a time

// Matches any character outside basic Latin, Latin-1 Supplement, Latin Extended-A/B,
// common punctuation/symbols, and digits — catches Chinese, Japanese, Korean, Cyrillic,
// Arabic, Hebrew, etc. while allowing normal accented Western brand names (e.g. "Nestlé").
const FOREIGN_CHAR_REGEX = /[^\u0000-\u024F\u2000-\u206F\u2010-\u2027\s]/;
const hasForeignChars = (str) => FOREIGN_CHAR_REGEX.test(str || '');

const EUA_STALE_MS = 30 * 24 * 60 * 60 * 1000; // 1 month
function euaAgeDays(updatedAtIso) {
  if (!updatedAtIso) return null;
  const ms = Date.now() - new Date(updatedAtIso).getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}
function isEuaStale(updatedAtIso) {
  if (!updatedAtIso) return false;
  return (Date.now() - new Date(updatedAtIso).getTime()) > EUA_STALE_MS;
}

const BulkKeepaModal = ({ brands: brandsProp, reviewedSet, brandEstimates, onClose, onMarkReviewed }) => {
  const [brands] = useState(brandsProp); // snapshot so the list doesn't shift while marking
  const [selected, setSelected] = useState(brands.length <= 50 ? brands : []);
  const [showLinks, setShowLinks] = useState(false);
  const [copied, setCopied] = useState(false);
  const [marking, setMarking] = useState(null); // 'all' | chunk index | null

  const isAllReviewed = (list) => list.length > 0 && list.every(b => reviewedSet?.has(b.toLowerCase()));

  const markReviewed = async (list, key) => {
    if (!list.length || marking !== null) return;
    setMarking(key);
    try {
      // send in batches of 500 so huge lists don't choke the request
      for (let i = 0; i < list.length; i += 500) {
        await onMarkReviewed?.(list.slice(i, i + 500));
      }
    } finally {
      setMarking(null);
    }
  };
  const [autoMarkReviewed, setAutoMarkReviewed] = useState(() => {
    try { return localStorage.getItem(LS_AUTO_MARK_REVIEWED) === 'true'; }
    catch { return false; }
  });
  const lastSelected = useRef(null);
  const filteredBrands = brands;

  const updateAutoMarkReviewed = (val) => {
    setAutoMarkReviewed(val);
    try { localStorage.setItem(LS_AUTO_MARK_REVIEWED, val ? 'true' : 'false'); } catch { }
  };

  const toggle = (brand, e) => {
    if (e?.shiftKey && lastSelected.current && lastSelected.current !== brand) {
      const fromIdx = filteredBrands.indexOf(lastSelected.current);
      const toIdx = filteredBrands.indexOf(brand);
      const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
      const rangebrands = filteredBrands.slice(start, end + 1);
      setSelected(prev => {
        const combined = [...new Set([...prev, ...rangebrands])];
        return combined.slice(0, KEEPA_LIMIT);
      });
    } else {
      setSelected(prev =>
        prev.includes(brand)
          ? prev.filter(b => b !== brand)
          : prev.length >= KEEPA_LIMIT ? prev : [...prev, brand]
      );
    }
    lastSelected.current = brand;
  };

  const selectAll = () => {
    setSelected(filteredBrands.slice(0, KEEPA_LIMIT));
  };

  const clearAll = () => setSelected([]);

  const chunks = useMemo(() => {
    const result = [];
    for (let i = 0; i < brands.length; i += KEEPA_LIMIT)
      result.push(brands.slice(i, i + KEEPA_LIMIT));
    return result;
  }, [brands]);

  const openAllLinks = () => setShowLinks(true);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[65vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-800 text-base">Bulk Source on Keepa</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Keepa allows max 50 brands per search · select up to 50, or generate all links.<br /><span className="text-blue-600 font-bold">SHIFT + CLICK to select a range</span>
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-500 transition">
            <X size={16} />
          </button>
        </div>

        {/* Controls */}
        <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoMarkReviewed}
              onChange={e => updateAutoMarkReviewed(e.target.checked)}
              className="accent-emerald-500"
            />
            Auto-mark opened brands as reviewed
          </label>
          <span className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full border ${selected.length === KEEPA_LIMIT
            ? 'bg-orange-50 text-orange-600 border-orange-200'
            : 'bg-gray-50 text-gray-500 border-gray-200'
            }`}>
            {selected.length} / {KEEPA_LIMIT} selected
          </span>
          <button
            onClick={clearAll}
            className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
          >
            Clear
          </button>
        </div>

        {/* Brand list */}
        <div className="overflow-y-auto flex-1 px-6 py-3">
          {filteredBrands.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-10">No brands in this category.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {filteredBrands.map(brand => {
                const isSelected = selected.includes(brand);
                const isDisabled = !isSelected && selected.length >= KEEPA_LIMIT;
                const isReviewed = reviewedSet?.has(brand.toLowerCase());
                const eua = brandEstimates?.[brand];
                return (
                  <button
                    key={brand}
                    onClick={(e) => toggle(brand, e)}
                    disabled={isDisabled}
                    className={`flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border transition select-none ${isSelected
                      ? 'bg-green-600 text-white border-green-600'
                      : isDisabled
                        ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                        : isReviewed
                          ? 'bg-blue-50 text-blue-800 border-blue-200 hover:border-blue-400'
                          : 'bg-green-50 text-green-700 border-green-300 hover:border-green-400 hover:text-green-600'
                      }`}
                  >
                    {brand}
                    {typeof eua === 'number' && (
                      <span className={`text-[10px] font-bold ${isSelected ? 'opacity-80' : 'opacity-60'}`}>
                        ({eua}% EUA)
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 flex flex-wrap gap-2 items-center">
          <div className="text-xs text-gray-400 flex-1">
            {chunks.length} link{chunks.length !== 1 ? 's' : ''} needed to cover all {brands.length} brands ({chunks.length} × 50)
          </div>
          <button
            onClick={() => setShowLinks(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-700 hover:bg-gray-900 text-white text-xs font-semibold rounded-lg transition"
          >
            <ExternalLink size={12} /> Generate All {chunks.length} Links
          </button>
          <a
            href={selected.length > 0 ? getBulkKeepaUrl(selected) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (autoMarkReviewed && selected.length > 0) markReviewed(selected, 'selected');
            }}
            className={`flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition ${selected.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}
          >
            <ExternalLink size={12} /> Open Selected in Keepa
          </a>
        </div>

        {showLinks && (
          <div className="absolute inset-0 bg-white rounded-2xl z-10 flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-base">All {chunks.length} Keepa Links</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allLinks = chunks.map(chunk => getBulkKeepaUrl(chunk)).join('\n');
                      navigator.clipboard.writeText(allLinks);
                      if (autoMarkReviewed) markReviewed(brands, 'all');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-gray-700 text-xs font-semibold rounded-lg transition"
                  >
                    {copied ? '✓ Copied!' : 'Copy All Links'}
                  </button>
                  <button
                    onClick={() => markReviewed(brands, 'all')}
                    disabled={marking !== null || isAllReviewed(brands)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 disabled:opacity-50 text-blue-700 text-xs font-semibold rounded-lg transition"
                  >
                    {marking === 'all' && <Loader2 size={12} className="animate-spin" />}
                    {isAllReviewed(brands) ? '✓ All Reviewed' : `Mark All ${brands.length} Reviewed`}
                  </button>
                  <button onClick={() => setShowLinks(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-500 transition">
                    <X size={16} />
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 cursor-pointer select-none mt-2">
                <input
                  type="checkbox"
                  checked={autoMarkReviewed}
                  onChange={e => updateAutoMarkReviewed(e.target.checked)}
                  className="accent-emerald-500"
                />
                Auto-mark opened brands as reviewed
              </label>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4 flex flex-col gap-3">
              {chunks.map((chunk, i) => {
                const markChunkReviewed = () => {
                  if (autoMarkReviewed) markReviewed(chunk, i);
                };
                const chunkDone = isAllReviewed(chunk);
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-xl transition group"
                  >
                    <a
                      href={getBulkKeepaUrl(chunk)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={markChunkReviewed}
                      className="text-sm font-semibold text-gray-700 group-hover:text-blue-700 flex-1"
                    >
                      Brands {i * 50 + 1}–{Math.min((i + 1) * 50, brands.length)}
                    </a>
                    <button
                      onClick={() => markReviewed(chunk, i)}
                      disabled={marking !== null || chunkDone}
                      className={`text-xs font-semibold px-2 py-1 rounded-md mr-2 transition ${chunkDone ? 'text-blue-600 bg-blue-50' : 'text-gray-600 bg-white border border-gray-200 hover:bg-blue-50 hover:text-blue-700'} disabled:cursor-default`}
                    >
                      {marking === i
                        ? <Loader2 size={11} className="animate-spin inline" />
                        : chunkDone ? '✓ Reviewed' : `Mark ${chunk.length} Reviewed`}
                    </button>
                    <button
                      onClick={() => { navigator.clipboard.writeText(getBulkKeepaUrl(chunk)); markChunkReviewed(); }}
                      className="text-xs text-gray-400 hover:text-blue-600 px-2"
                      title="Copy this link"
                    >
                      Copy
                    </button>
                    <a
                      href={getBulkKeepaUrl(chunk)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={markChunkReviewed}
                      className="text-xs text-gray-400 group-hover:text-blue-500 flex items-center gap-1"
                    >
                      Open in Keepa <ExternalLink size={11} />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BulkReviewModal = ({ onClose, onSubmit }) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parseNames = (raw) =>
    raw.split(/[\n,]/).map(s => s.trim()).filter(Boolean);

  const handleSubmit = async () => {
    const names = parseNames(text);
    if (names.length === 0) return;
    setSubmitting(true);
    const data = await onSubmit(names);
    setResult(data);
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-800 text-base">Bulk Mark as Reviewed</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-500 transition">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4">
          <p className="text-xs text-gray-400 mb-2">Paste brand names, one per line or comma-separated. Matching isn't case-sensitive.</p>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={8}
            placeholder={"Nike\nAdidas, Puma"}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {result && (
            <div className="mt-2 text-xs">
              <p className="text-emerald-600 font-semibold">{result.matched_count} brand{result.matched_count !== 1 ? 's' : ''} marked reviewed.</p>
              {result.unmatched?.length > 0 && (
                <p className="text-gray-400 mt-1">Not found: {result.unmatched.join(', ')}</p>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition">
            Close
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !text.trim()}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition"
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : null}
            Mark Reviewed
          </button>
        </div>
      </div>
    </div>
  );
};

const LockedUngatedBrandsWidget = ({ minPlan = 'MSP Pro' }) => (
  <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden p-6">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
        <Shield size={22} className="text-white" />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-gray-800 mb-1">My Amazon Ungated Brands</h3>
        <p className="flex items-center gap-2 text-gray-700 font-semibold text-sm mb-3">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
          {minPlan} Plan Required
        </p>
        <p className="text-gray-500 text-sm mb-4">
          Track your ungated brands, bulk-source them on Keepa, and auto-discover new ones with the Ungate Crawler - available on MSP Pro and above.
        </p>
        <button
          onClick={() => window.location.href = '/pricing'}
          className="px-5 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold text-sm hover:opacity-90 transition shadow-lg"
        >
          Upgrade to Unlock
        </button>
      </div>
    </div>
  </div>
);

const HistoricalLeadsModal = ({ onClose }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [page, setPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);

  const migrateLocalStorageLeads = async () => {
    let localLeads = {};
    try {
      localLeads = JSON.parse(localStorage.getItem(SK_HISTORICAL_LEADS) || "{}");
    } catch {
      return; // corrupted, nothing we can safely migrate
    }

    const leadList = Object.values(localLeads);
    if (leadList.length === 0) return;

    setMigrating(true);
    try {
      const res = await authService.authenticatedFetch(`${API_BASE}/historical-leads/bulk-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leads: leadList }),
      });
      if (res.ok) {
        // Only clear once the server confirms the save, so a failed request
        // leaves the local copy intact to retry next time the modal opens.
        localStorage.removeItem(SK_HISTORICAL_LEADS);
      }
    } catch {
      /* leave localStorage intact, will retry next open */
    } finally {
      setMigrating(false);
    }
  };

  const fetchPage = async (pageNum) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageNum, page_size: LEADS_PAGE_SIZE });
      if (categoryFilter) params.set('category', categoryFilter);
      if (searchTerm) params.set('search', searchTerm);
      const r = await authService.authenticatedFetch(`${API_BASE}/historical-leads?${params.toString()}`);
      const d = await r.json();
      setLeads(d.leads || []);
      setTotalLeads(d.total || 0);
    } catch {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const r = await authService.authenticatedFetch(`${API_BASE}/historical-leads/categories`);
      const d = await r.json();
      setAvailableCategories(d.categories || []);
    } catch {
      setAvailableCategories([]);
    }
  };

  useEffect(() => {
    (async () => {
      await migrateLocalStorageLeads();
      await fetchCategories();
      await fetchPage(1);
    })();
  }, []);

  // Debounce the free-text search so we're not firing a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Any time a filter changes, go back to page 1
  useEffect(() => {
    setPage(1);
  }, [categoryFilter, searchTerm]);

  useEffect(() => {
    fetchPage(page);
  }, [page, categoryFilter, searchTerm]);

  // Server already returns this page sorted by found_at desc — no client re-sort needed
  const leadList = leads;
  const totalPages = Math.max(1, Math.ceil(totalLeads / LEADS_PAGE_SIZE));

  const removeLead = async (asin) => {
    setLeads(prev => prev.filter(l => l.asin !== asin));
    setTotalLeads(t => Math.max(0, t - 1));
    try {
      await authService.authenticatedFetch(`${API_BASE}/historical-leads/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ asin }),
      });
    } catch { }
  };

  const removeAll = async () => {
    if (!window.confirm('Remove all historical leads? This cannot be undone.')) return;
    setLeads([]);
    setTotalLeads(0);
    setPage(1);
    try {
      await authService.authenticatedFetch(`${API_BASE}/historical-leads/delete-all`, { method: 'POST' });
    } catch { }
  };

  const exportCsv = async () => {
    if (totalLeads === 0) return;
    let allLeads = [];
    try {
      const EXPORT_PAGE_SIZE = 200; // matches the server-side page_size cap
      let p = 1;
      let expectedTotal = totalLeads;
      while (true) {
        const params = new URLSearchParams({ page: p, page_size: EXPORT_PAGE_SIZE });
        if (categoryFilter) params.set('category', categoryFilter);
        if (searchTerm) params.set('search', searchTerm);
        const r = await authService.authenticatedFetch(`${API_BASE}/historical-leads?${params.toString()}`);
        const d = await r.json();
        const batch = d.leads || [];
        expectedTotal = d.total ?? expectedTotal;
        allLeads = allLeads.concat(batch);
        if (batch.length < EXPORT_PAGE_SIZE || allLeads.length >= expectedTotal) break;
        p += 1;
      }
    } catch {
      allLeads = leadList; // fall back to current page only
    }

    const headers = ["ASIN", "Brand", "Title", "Category", "Price", "BSR", "BSR Drops/mo", "Monthly Sold", "Found At"];
    const rows = allLeads.map(p => [
      p.asin,
      `"${(p.brand || '').replace(/"/g, '""')}"`,
      `"${(p.title || '').replace(/"/g, '""')}"`,
      `"${(p.category || '').replace(/"/g, '""')}"`,
      p.az_price > 0 ? Number(p.az_price).toFixed(2) : '',
      p.sales_rank > 0 ? p.sales_rank : '',
      p.sales_rank_drops_30 > 0 ? p.sales_rank_drops_30 : '',
      p.keepa_monthly_sold > 0 ? p.keepa_monthly_sold : '',
      p.found_at || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "historical_ungated_leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 mt-10"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-bold text-gray-800 text-base">Historical Potential Leads</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {totalLeads} lead{totalLeads !== 1 ? 's' : ''} found by the crawler over time
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              disabled={leadList.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 disabled:opacity-40 text-emerald-700 text-xs font-semibold rounded-lg transition"
            >
              <Download size={12} /> Export CSV
            </button>
            <button
              onClick={removeAll}
              disabled={leadList.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 disabled:opacity-40 text-red-700 text-xs font-semibold rounded-lg transition"
            >
              Remove All
            </button>
            <button onClick={onClose} className="flex items-center gap-1.5 py-1.5 bg-white-100 hover:bg-white-200 disabled:opacity-40 text-gray-700 text-xs font-semibold rounded-lg transition">
              Close
            </button>
          </div>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 flex gap-2 items-center flex-wrap">
          <div className="flex-1 min-w-[160px] relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300" />
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by brand, title, or ASIN…"
              className="w-full pl-7 pr-6 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                <X size={10} />
              </button>
            )}
          </div>
          {availableCategories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-400"
            >
              <option value="">All Categories</option>
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
          {(categoryFilter || searchTerm) && (
            <button
              onClick={() => { setSearchInput(''); setCategoryFilter(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 font-semibold"
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 flex flex-col gap-2">
          {loading ? (
            <div className="py-16 flex flex-col items-center gap-3 text-gray-400">
              <Loader2 size={24} className="animate-spin" />
              <span className="text-sm">{migrating ? "Importing your older leads…" : "Loading leads…"}</span>
            </div>
          ) : leadList.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-16">No historical leads yet. Run the Ungate Crawler to start finding some.</p>
          ) : (
            leadList.map(p => (
              <div key={p.asin} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 relative">
                <button
                  onClick={() => removeLead(p.asin)}
                  title="Remove this lead"
                  className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full bg-red-100 hover:bg-red-200 text-red-500 flex items-center justify-center text-[9px] font-bold"
                >
                  ✕
                </button>
                {p.image
                  ? <img src={p.image} alt="" onClick={() => { window.open(`https://amazon${authService.getAmazonMarketplace()}/dp/${p.asin}`) }} className="w-11 h-11 cursor-pointer object-contain rounded-lg bg-white flex-shrink-0" />
                  : <div className="w-11 h-11 rounded-lg bg-gray-100 flex-shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate cursor-pointer" onClick={() => { window.open(`https://amazon${authService.getAmazonMarketplace()}/dp/${p.asin}`) }}>{p.title || p.asin}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    {p.brand && <span className="text-[11px] font-bold text-gray-600">{p.brand}</span>}
                    {p.asin && <span className="text-[11px] font-bold text-gray-500 underline cursor-pointer" onClick={() => { window.open(`https://amazon${authService.getAmazonMarketplace()}/dp/${p.asin}`) }}>{p.asin}</span>}
                    {p.az_price > 0 && <span className="text-[11px] text-gray-500">${Number(p.az_price).toFixed(2)}</span>}
                    {p.sales_rank_drops_30 > 0 && (
                      <span className="text-[9px] bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5">📉 {p.sales_rank_drops_30} drops/mo</span>
                    )}
                    {p.keepa_monthly_sold > 0 && (
                      <span className="text-[9px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5">✅ {Number(p.keepa_monthly_sold).toLocaleString()} sold/mo</span>
                    )}
                  </div>
                  {p.found_at && (
                    <p className="text-[10px] text-gray-400 mt-0.5">Found {new Date(p.found_at).toLocaleDateString()}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => window.open(`https://www.amazon${authService.getAmazonMarketplace()}/dp/${p.asin}`, '_blank')}
                    title="Open product on Amazon"
                    className="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center"
                  >
                    <ExternalLink size={13} className="text-gray-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && totalLeads > 0 && (
          <div className="flex items-center justify-center gap-3 py-3 border-t border-gray-100">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 disabled:opacity-40 rounded-lg transition"
            >
              ← Prev
            </button>
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages} · {totalLeads.toLocaleString()} total leads
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 disabled:opacity-40 rounded-lg transition"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const UngatedBrandsWidget = ({ user, hasCrawlerAccess = false }) => {
  const [brands, setBrands] = useState([]);
  const [hiddenBrands, setHiddenBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [confirmHide, setConfirmHide] = useState(null);
  const [hidingBrand, setHidingBrand] = useState(null);
  const [reportingBrand, setReportingBrand] = useState(null);
  const [brandCategories, setBrandCategories] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [showBulkKeepa, setShowBulkKeepa] = useState(false);
  const [showCrawler, setShowCrawler] = useState(false);
  const [hideBadBrands, setHideBadBrands] = useState(false);
  const [badBrands, setBadBrands] = useState(new Set());
  const [hideForeignBrands, setHideForeignBrands] = useState(false);
  const [reviewedBrands, setReviewedBrands] = useState([]);
  const [reviewFilter, setReviewFilter] = useState('all'); // 'all' | 'reviewed' | 'unreviewed'
  const [markingBrand, setMarkingBrand] = useState(null);
  const [showBulkReview, setShowBulkReview] = useState(false);
  const [showHiddenModal, setShowHiddenModal] = useState(false);
  const [showHistoricalLeads, setShowHistoricalLeads] = useState(false);
  const [visibleBrandCount, setVisibleBrandCount] = useState(BRAND_PAGE_SIZE);
  const [brandEstimates, setBrandEstimates] = useState({}); // brand -> pct (0-100)
  const [brandEstimateUpdatedAt, setBrandEstimateUpdatedAt] = useState({}); // brand -> ISO timestamp
  const [minEuaFilter, setMinEuaFilter] = useState(''); // '' = no filter, else 0-100
  const [fillingEstimates, setFillingEstimates] = useState(false);
  const [fillProgress, setFillProgress] = useState({ done: 0, total: 0, currentBrand: '', brandChecked: 0, brandTotal: 0 });
  const [showFillConfirm, setShowFillConfirm] = useState(false);
  const fillStopRef = useRef(false);

  const hiddenSet = useMemo(() => new Set(hiddenBrands), [hiddenBrands]);
  const reviewedSet = useMemo(() => new Set(reviewedBrands.map(b => b.toLowerCase())), [reviewedBrands]);
  const allCategories = useMemo(() =>
    [...new Set(Object.values(brandCategories).filter(Boolean))]
      .filter(c => AMAZON_CATEGORY_WHITELIST.has(c))
      .sort(),
    [brandCategories]
  );

  const visibleBrands = useMemo(() =>
    brands.filter(b => !hiddenSet.has(b)),
    [brands, hiddenSet]
  );

  const filtered = useMemo(() => {
    const source = visibleBrands; // hidden brands now live only in the Hidden modal
    const terms = search.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
    return source.filter(b => {
      const matchesSearch = terms.length === 0 || terms.some(t => b.toLowerCase().includes(t));
      const matchesCategory = selectedCategories.length === 0 ||
        selectedCategories.includes(brandCategories[b]);
      const matchesBad = !hideBadBrands || !badBrands.has(b);
      const matchesForeign = !hideForeignBrands || !hasForeignChars(b);
      const isReviewed = reviewedSet.has(b.toLowerCase());
      const matchesReview = reviewFilter === 'all' ||
        (reviewFilter === 'reviewed' && isReviewed) ||
        (reviewFilter === 'unreviewed' && !isReviewed);
      const minEua = minEuaFilter === '' ? null : Number(minEuaFilter);
      const matchesMinEua = minEua === null ||
        (typeof brandEstimates[b] === 'number' && brandEstimates[b] >= minEua);
      return matchesSearch && matchesCategory && matchesBad && matchesForeign && matchesReview && matchesMinEua;
    });
  }, [visibleBrands, search, selectedCategories, brandCategories, hideBadBrands, badBrands, hideForeignBrands, reviewedSet, reviewFilter, minEuaFilter, brandEstimates]);

  const PAGE_LIMIT = 1000;

  const fetchBrands = async () => {
    setLoading(true);
    // reset everything before the fresh pull
    setBrands([]);
    setHiddenBrands([]);
    setReviewedBrands([]);
    setBrandCategories({});
    setBrandEstimates({});
    setBrandEstimateUpdatedAt({});
    setBadBrands(new Set());

    try {
      const SKIP_SYNC_EMAILS = ['marvelumeh@gmail.com', 'someone@else.com'];

      // 1. One-time sync so the table has any newly-found ungated brands
      const email = (user?.email || '').trim().toLowerCase();

      if (!SKIP_SYNC_EMAILS.includes(email)) {
        authService.authenticatedFetch(`${API_BASE}/user/ungated-brands/sync`, { method: 'POST' });
      }

      // 2. Page through the table, 1000 brands at a time
      let offset = 0;
      let hasMore = true;
      const seen = new Set();

      while (hasMore) {
        const res = await authService.authenticatedFetch(
          `${API_BASE}/user/ungated-brands/page?offset=${offset}&limit=${PAGE_LIMIT}`
        );
        const data = await res.json();
        const rows = data.rows || [];

        const pageBrands = [];
        const pageHidden = [];
        const pageReviewed = [];
        const pageCategories = {};
        const pageEstimates = {};
        const pageEstimateUpdated = {};

        rows.forEach(r => {
          const key = r.brand.toLowerCase().trim();
          if (seen.has(key)) return;
          seen.add(key);

          if (r.hidden) pageHidden.push(r.brand);
          else pageBrands.push(r.brand);
          if (r.reviewed) pageReviewed.push(r.brand);
          if (r.category) pageCategories[r.brand] = r.category;
          if (r.ungate_estimate_pct !== null && r.ungate_estimate_pct !== undefined) {
            pageEstimates[r.brand] = r.ungate_estimate_pct;
          }
          if (r.ungate_estimate_updated_at) {
            pageEstimateUpdated[r.brand] = r.ungate_estimate_updated_at;
          }
        });

        // Append this page into state — UI updates as each page arrives
        setBrands(prev => [...prev, ...pageBrands]);
        setHiddenBrands(prev => [...prev, ...pageHidden]);
        setReviewedBrands(prev => [...prev, ...pageReviewed]);
        setBrandCategories(prev => ({ ...prev, ...pageCategories }));
        setBrandEstimates(prev => ({ ...prev, ...pageEstimates }));
        setBrandEstimateUpdatedAt(prev => ({ ...prev, ...pageEstimateUpdated }));
        setLoaded(true);

        // Check "bad brands" for just this page (fire and forget)
        if (pageBrands.length > 0) {
          authService.authenticatedFetch(`${API_BASE}/brands/my-bad-brands`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ brands: pageBrands })
          })
            .then(r => r.json())
            .then(d => setBadBrands(prev => new Set([...prev, ...(d.brands || [])])))
            .catch(() => { });
        }

        hasMore = !!data.has_more;
        offset = data.next_offset ?? (offset + rows.length);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && !loaded) fetchBrands();
  }, [expanded]);

  // Whenever the filtered brand list changes (new search, new filter, refresh, etc.)
  // reset how many badges we render, so we don't keep 10k stale nodes mounted.
  useEffect(() => {
    setVisibleBrandCount(BRAND_PAGE_SIZE);
  }, [filtered]);

  const handleHide = async (brand) => {
    setHidingBrand(brand);
    setConfirmHide(null);
    try {
      const res = await authService.authenticatedFetch(
        `${API_BASE}/user/ungated-brands/hide`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_name: brand, hidden: true })
        }
      );
      const data = await res.json();
      if (data.success) setHiddenBrands(data.hidden_brands || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHidingBrand(null);
    }
  };

  const handleRestore = async (brand) => {
    setHidingBrand(brand);
    try {
      const res = await authService.authenticatedFetch(
        `${API_BASE}/user/ungated-brands/hide`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_name: brand, hidden: false })
        }
      );
      const data = await res.json();
      if (data.success) setHiddenBrands(data.hidden_brands || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHidingBrand(null);
    }
  };

  const handleMarkReviewed = async (brand, reviewed) => {
    setMarkingBrand(brand);
    try {
      const res = await authService.authenticatedFetch(
        `${API_BASE}/user/ungated-brands/review`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_name: brand, reviewed })
        }
      );
      const data = await res.json();
      if (data.success) setReviewedBrands(data.reviewed_brands || []);
    } catch (e) {
      console.error(e);
    } finally {
      setMarkingBrand(null);
    }
  };

  const handleBulkReview = async (brandNames) => {
    try {
      const res = await authService.authenticatedFetch(
        `${API_BASE}/user/ungated-brands/review-bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand_names: brandNames, reviewed: true })
        }
      );
      const data = await res.json();
      if (data.success) setReviewedBrands(data.reviewed_brands || []);
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const fillAllEstimates = useCallback(async (skipExisting = false) => {
    fillStopRef.current = false;
    setFillingEstimates(true);

    const SKIP_IF_UPDATED_WITHIN_MS = 48 * 60 * 60 * 1000; // 48 hours
    const now = Date.now();
    const targets = filtered.filter(b => {
      const hasExistingEstimate = typeof brandEstimates[b] === 'number';
      if (skipExisting && hasExistingEstimate) return false; // user chose to skip any brand with an EUA already, regardless of age
      const updatedAt = brandEstimateUpdatedAt[b];
      if (!updatedAt) return true; // never estimated — always include
      return (now - new Date(updatedAt).getTime()) > SKIP_IF_UPDATED_WITHIN_MS;
    });

    setFillProgress({ done: 0, total: targets.length, currentBrand: '', brandChecked: 0, brandTotal: 0 });

    for (let i = 0; i < targets.length; i++) {
      if (fillStopRef.current) break;
      const brand = targets[i];
      setFillProgress({ done: i, total: targets.length, currentBrand: brand, brandChecked: 0, brandTotal: 0 });

      try {
        const asinEndpoint = isUsMarketplaceUser(user)
          ? `${API_BASE}/user/ungated-brands/v2/brand-asins`
          : `${API_BASE}/user/ungated-brands/v2/brand-asins-intl`;
        const asinRes = await authService.authenticatedFetch(asinEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand }),
        });
        const asinData = await asinRes.json();
        const asins = asinData.asins || [];

        setFillProgress(p => ({ ...p, brandTotal: asins.length }));

        let checked = 0, ungated = 0;
        for (let j = 0; j < asins.length; j += 10) {
          if (fillStopRef.current) break;
          const chunk = asins.slice(j, j + 10);
          const gateRes = await authService.authenticatedFetch(`${API_BASE}/a2a/check-gated-bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ asins: chunk, skip_cache: true }),
          });
          const gateData = await gateRes.json();
          const results = gateData.results || {};
          if (Object.values(results).some(s => s === 'needs_login')) {
            fillStopRef.current = true;
            break;
          }
          Object.values(results).forEach(s => {
            checked += 1;
            if (s === 'ungated') ungated += 1;
          });
          setFillProgress(p => ({ ...p, brandChecked: checked }));
          await new Promise(r => setTimeout(r, 4500)); // same SP-API pace as the crawler
        }

        if (checked > 0) {
          const pct = Math.round((ungated / checked) * 1000) / 10;
          const res = await authService.authenticatedFetch(`${API_BASE}/user/ungated-brands/v2/set-estimate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ brand, checked_count: checked, ungated_count: ungated }),
          });
          const data = await res.json();
          setBrandEstimates(prev => ({ ...prev, [brand]: pct }));
          if (data.ungate_estimate_updated_at) {
            setBrandEstimateUpdatedAt(prev => ({ ...prev, [brand]: data.ungate_estimate_updated_at }));
          }
        }
      } catch { /* skip brand on error, move to next */ }
    }

    setFillProgress(p => ({ ...p, done: targets.length, currentBrand: '' }));
    setFillingEstimates(false);
  }, [filtered, brandEstimateUpdatedAt, brandEstimates, user]);

  const stopFillEstimates = useCallback(() => {
    fillStopRef.current = true;
    setFillingEstimates(false);
  }, []);

  const handleReport = async (brand) => {
    if (reportingBrand) return;
    setReportingBrand(brand);
    await new Promise(r => setTimeout(r, 2000));
    handleHide(brand);
    authService.authenticatedFetch(
      `${API_BASE}/brands/check-quality`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_name: brand })
      }
    ).catch(() => { });
    setReportingBrand(null);
  };

  const exportCsv = () => {
    const headers = ['Brand Name', 'Category', 'EUA %', 'EUA Last Updated'];
    const rows = filtered.map(b => [
      `"${b.replace(/"/g, '""')}"`,
      `"${(brandCategories[b] || '').replace(/"/g, '""')}"`,
      typeof brandEstimates[b] === 'number' ? brandEstimates[b] : '',
      brandEstimateUpdatedAt[b] || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ungated-brands.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const mspMinPlanLabel = user?.amazon_marketplace === 'US' ? 'MSP Pro' : 'MSP Beginner';

  if (!hasCrawlerAccess) {
    return <LockedUngatedBrandsWidget minPlan={mspMinPlanLabel} />;
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
        <style>{`
          @keyframes uc-arrow-nudge {
            0%, 100% { transform: translateX(0); opacity: 0.5; }
            50% { transform: translateX(5px); opacity: 1; }
          }
          .uc-arrow-nudge-1 {
            animation: uc-arrow-nudge 1s ease-in-out infinite;
          }
          .uc-arrow-nudge-2 {
            animation: uc-arrow-nudge 1s ease-in-out infinite;
            margin-left: -12px;
          }
          @keyframes uc-crawler-glow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6); }
            50% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
          }
          .uc-crawler-glow-btn {
            animation: uc-crawler-glow 1.8s ease-in-out infinite;
          }
          @keyframes uc-badge-pop {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.12); }
          }
          .uc-badge-pop {
            animation: uc-badge-pop 1.2s ease-in-out infinite;
          }
        `}</style>

        {/* Header toggle */}
        <div
          onClick={() => setExpanded(o => !o)}
          className="w-full flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r from-emerald-50 to-blue-50 hover:from-emerald-100 hover:to-blue-100 border-2 border-emerald-200 hover:border-emerald-300 rounded-t-xl shadow-sm hover:shadow-md transition-all text-left cursor-pointer"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
            <Shield size={15} className="text-white" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-gray-800 text-sm">My Amazon Ungated Brands</span>
            {!expanded && (
              <span className="text-xs text-emerald-700 font-medium truncate flex items-center gap-1">
                Click here to see brands you can sell!
                <span className="flex items-center flex-shrink-0">
                  <ChevronLeft size={18} className="uc-arrow-nudge-1" />
                  <ChevronLeft size={18} className="uc-arrow-nudge-2" />
                </span>
              </span>
            )}
          </div>
          {loaded && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-200 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-700 flex-shrink-0">
              {visibleBrands.length.toLocaleString()} ungated
              {loading && (
                <>
                  <Loader2 size={10} className="animate-spin" />
                  <span className="opacity-80">still loading…</span>
                </>
              )}
            </span>
          )}
          {loaded && (
            <button
              onClick={(e) => { e.stopPropagation(); window.open('https://www.youtube.com/watch?v=U095qwucBpg', '_blank'); }}
              className="text-[11px] text-blue-500 hover:text-blue-700 underline underline-offset-2 hidden sm:inline"
              title="See which exact ASINs you're ungated for"
            >
              ▶ Which ASINs am I ungated for?
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(o => !o); }}
            className={`ml-auto flex items-center gap-1 px-3.5 py-2 rounded-lg text-xs font-bold transition-all flex-shrink-0 ${expanded
              ? 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'
              : 'bg-emerald-600 text-white shadow-md hover:shadow-lg hover:bg-emerald-700 border border-emerald-700 hover:-translate-y-0.5'
              }`}
          >
            {expanded ? 'Hide' : 'View Brands'}
            <ChevronRight size={12} className={`transition-transform ${expanded ? '-rotate-90' : 'rotate-90'}`} />
          </button>
        </div>

        {expanded && (
          <div className="border-t border-gray-50">

            {/* Search + actions */}
            <div className="px-4 py-3 flex gap-2 items-center border-b border-gray-50 flex-wrap">
              <div className="flex-1 min-w-[160px] relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search brands…"
                  className="w-full pl-8 pr-8 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <label className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none ${loading || badBrands.size === 0 && !loading ? 'text-gray-400' : 'text-gray-600'}`}>
                <input
                  type="checkbox"
                  checked={hideBadBrands}
                  onChange={e => setHideBadBrands(e.target.checked)}
                  disabled={loading}
                  className="accent-red-500 disabled:opacity-50"
                />
                {loading
                  ? <><Loader2 size={11} className="animate-spin" /> Checking bad brands…</>
                  : `Hide bad brands (${badBrands.size})`
                }
              </label>
              <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer select-none text-gray-600">
                <input
                  type="checkbox"
                  checked={hideForeignBrands}
                  onChange={e => setHideForeignBrands(e.target.checked)}
                  className="accent-orange-500"
                />
                Hide foreign-letter brands
              </label>
              {allCategories.length === 0 && (
                <span className="text-xs text-gray-400 italic px-1">
                  Category filters will unlock once you find ungated brands
                </span>
              )}
              {allCategories.length > 0 && (
                <div className="relative">
                  <button
                    data-cat-btn
                    onClick={() => setCategoryDropdownOpen(o => !o)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-200 transition"
                  >
                    {selectedCategories.length > 0
                      ? `${selectedCategories.length} categor${selectedCategories.length > 1 ? 'ies' : 'y'}`
                      : 'All Categories'}
                    <ChevronRight size={10} className="rotate-90" />
                  </button>
                  {categoryDropdownOpen && (
                    <>
                      <div className="fixed inset-0 z-[99998]" onClick={() => setCategoryDropdownOpen(false)} />
                      <div className="fixed z-[99999] bg-white border border-gray-200 rounded-xl shadow-lg min-w-[200px] max-h-64 overflow-y-auto"
                        style={{
                          top: (() => { const el = document.querySelector('[data-cat-btn]'); if (!el) return 0; const r = el.getBoundingClientRect(); return r.bottom - 35; })(),
                          left: (() => { const el = document.querySelector('[data-cat-btn]'); if (!el) return 0; const r = el.getBoundingClientRect(); return r.left - 110; })(),
                        }}
                      >
                        <div className="p-2 border-b border-gray-100 flex justify-between items-center">
                          <span className="text-xs font-semibold text-gray-500">Filter by Category</span>
                          {selectedCategories.length > 0 && (
                            <button
                              onClick={() => setSelectedCategories([])}
                              className="text-xs text-red-400 hover:text-red-600 font-semibold"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {allCategories.map(cat => (
                          <label
                            key={cat}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-xs text-gray-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(cat)}
                              onChange={() =>
                                setSelectedCategories(prev =>
                                  prev.includes(cat)
                                    ? prev.filter(c => c !== cat)
                                    : [...prev, cat]
                                )
                              }
                              className="accent-emerald-500"
                            />
                            {cat}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Hidden brands popup trigger */}
              <button
                onClick={() => setShowHiddenModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition border bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-200"
              >
                Hidden ({hiddenBrands.length})
              </button>
              <button
                onClick={() => setShowBulkReview(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition border bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
              >
                Mark Reviewed (bulk)
              </button>
              <button
                onClick={fillingEstimates ? stopFillEstimates : () => setShowFillConfirm(true)}
                disabled={filtered.length === 0}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition border disabled:opacity-40 ${fillingEstimates ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200' : 'bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border-yellow-200'}`}
                title={fillingEstimates && fillProgress.currentBrand ? `Checking: ${fillProgress.currentBrand} (${fillProgress.brandChecked}/${fillProgress.brandTotal} ASINs)` : "Checks every brand's full ASIN list and computes its Estimated Ungated % (EUA)"}
              >
                {fillingEstimates
                  ? <>■ Stop Filling ({fillProgress.done}/{fillProgress.total}){fillProgress.currentBrand ? <span className="opacity-70"> · {fillProgress.currentBrand} {fillProgress.brandTotal > 0 ? `(${Math.round((fillProgress.brandChecked / fillProgress.brandTotal) * 100)}%)` : ''}</span> : ''}</>
                  : <>Fill All Estimated Ungated Asins</>}
              </button>
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-semibold text-gray-500 cursor-help" title="EUA = Estimated Ungated Asins — % of a brand's checked ASINs that came back ungated">Min EUA%</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={minEuaFilter}
                  onChange={e => setMinEuaFilter(e.target.value)}
                  placeholder="—"
                  className="w-16 px-2 py-1.5 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                {minEuaFilter !== '' && (
                  <button
                    onClick={() => setMinEuaFilter('')}
                    className="text-xs text-gray-400 hover:text-gray-600"
                    title="Clear Min EUA filter"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 text-xs font-semibold">
                {['all', 'unreviewed', 'reviewed'].map(f => (
                  <button
                    key={f}
                    onClick={() => setReviewFilter(f)}
                    className={`px-2.5 py-1 rounded-md capitalize transition ${reviewFilter === f ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              {(
                <>
                  <button
                    onClick={exportCsv}
                    disabled={filtered.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-xs font-semibold rounded-lg transition"
                  >
                    <Download size={12} /> Export CSV
                  </button>
                  <button
                    onClick={fetchBrands}
                    disabled={loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition"
                  >
                    {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Refresh
                  </button>
                  <button
                    onClick={() => setShowBulkKeepa(true)}
                    disabled={filtered.length === 0}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg transition"
                  >
                    <ExternalLink size={12} /> Bulk Source All Brands ({filtered.length})
                  </button>
                  <button
                    onClick={() => setShowCrawler(true)}
                    disabled={!user?.amazon_linked_account || Object.keys(user.amazon_linked_account || {}).length === 0}
                    title={!user?.amazon_linked_account || Object.keys(user.amazon_linked_account || {}).length === 0 ? 'Link your Amazon account to use the crawler' : 'Automatically discovers new ungated brands for you'}
                    className={`relative flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:bg-gray-200 disabled:from-gray-200 disabled:to-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 ${(!user?.amazon_linked_account || Object.keys(user.amazon_linked_account || {}).length === 0) ? '' : 'uc-crawler-glow-btn'}`}
                  >
                    <svg height={16} width={16} fill="#ffffff" version="1.1" id="Capa_1" viewBox="20 20 68 68" stroke="#ffffff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <g> <path style={{ scale: '1.5' }} d="M48.161,33.825l0.672,0.447c-1.121,0.673-2.912,1.12-5.377,1.567V34.72c2.017-2.017,3.137-4.704,3.137-7.616h1.347v-5.376 h-4.033v5.376h1.344c0,2.465-0.896,4.479-2.24,6.271l-3.584-4.032h-7.617l-3.359,3.81c-1.344-1.568-2.24-3.81-2.24-6.272h1.344 v-5.376h-4.031v5.601h1.344c0,2.912,1.12,5.601,3.136,7.616v1.119c-2.24-0.447-4.032-0.896-5.376-1.567l0.672-0.447l-2.688-4.704 l-3.584,2.016l2.688,4.704l1.344-0.896c1.568,1.119,4.032,1.792,6.944,2.24v2.463c-1.344,1.346-2.464,3.139-2.912,5.151h-1.567 v5.375h4.031v-5.149h-0.896c0.448-1.344,1.12-2.688,2.016-3.81l3.136,3.81h7.617l3.137-3.81c0.896,1.119,1.566,2.466,2.017,3.81 H43.68v5.377h4.031v-5.377h-1.568c-0.447-2.019-1.565-3.81-2.91-5.15v-2.465c2.91-0.447,5.375-1.346,6.943-2.241l1.344,0.896 l2.688-4.702l-3.584-2.018L48.161,33.825z"></path>  </g> </g> </g></svg> Ungate Crawler
                  </button>
                  <button
                    onClick={() => setShowHistoricalLeads(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs font-semibold rounded-lg transition"
                  >
                    🎯 Historical Potential Leads
                  </button>
                  <span className="text-[10px] text-gray-400" title="Estimated Ungated Asins — % of a brand's checked ASINs that came back ungated">*EUA = Estimated Ungated Asins</span>
                </>
              )}
            </div>

            {/* Brand pills */}
            {loading && brands.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400 flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" /> Loading brands…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-400">
                {search
                  ? `No brands matching "${search}"`
                  : !user?.amazon_linked_account ? (
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-sm text-gray-500">Link your Amazon account to see ungated brands.</p>
                      <button
                        onClick={() => window.open(`https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${encodeURIComponent(authService.credentials?.email || '')}`, "_blank")}
                        style={{ border: "none", background: "transparent", cursor: "pointer" }}
                      >
                        <img src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_101x46.png" style={{ height: 32 }} alt="Link Amazon Account" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      {!user?.amazon_linked_account || Object.keys(user.amazon_linked_account || {}).length === 0 ? (
                        <>
                          <p className="text-sm text-gray-500">Link your Amazon account to start finding ungated brands.</p>
                          <button
                            onClick={() => window.open(`https://sellercentral.amazon${authService.getAmazonMarketplace()}/apps/authorize/consent?application_id=amzn1.sp.solution.18aecca2-d85f-492c-b0ba-d121066f7ef8&state=${encodeURIComponent(authService.credentials?.email || '')}`, "_blank")}
                            style={{ border: "none", background: "transparent", cursor: "pointer" }}
                          >
                            <img src="https://images-na.ssl-images-amazon.com/images/G/01/lwa/btnLWA_gold_101x46.png" style={{ height: 32 }} alt="Link Amazon Account" />
                          </button>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-500">No ungated brands found yet.</p>
                          <p className="text-xs text-gray-400">
                            Run any scan to discover ungated brands, or use the{' '}
                            <button onClick={() => setShowCrawler(true)} className="text-emerald-600 hover:text-emerald-700 font-semibold underline underline-offset-2 transition">
                              Ungate Crawler
                            </button>.
                          </p>
                        </>
                      )}
                    </div>
                  )}
              </div>
            ) : (
              <div
                className="overflow-y-auto px-4 py-3"
                style={{ maxHeight: 320 }}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                  // When within 200px of the bottom, reveal the next page of badges
                  if (scrollHeight - scrollTop - clientHeight < 200) {
                    setVisibleBrandCount(c => Math.min(c + BRAND_PAGE_SIZE, filtered.length));
                  }
                }}
              >
                <div className="flex flex-wrap gap-2">
                  {filtered.slice(0, visibleBrandCount).map(brand => {
                    const isReviewed = reviewedSet.has(brand.toLowerCase());
                    return (
                      <div key={brand} className="relative">
                        <div className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border transition group ${isReviewed
                          ? 'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 hover:border-blue-400'
                          : 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-400'
                          }`}>
                          {confirmHide === brand ? (
                            <>
                              <span className="text-xs text-gray-600">Hide?</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleReport(brand); }}
                                disabled={!!reportingBrand}
                                className="text-gray-300 hover:text-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                title="Report brand"
                              >
                                {reportingBrand === brand
                                  ? <Loader2 size={11} className="animate-spin text-orange-400" />
                                  : <TriangleAlert size={16} />}
                              </button>
                              <button
                                onClick={() => handleHide(brand)}
                                className="px-1.5 py-0.5 bg-red-500 hover:bg-red-600 text-white text-[10px] font-semibold rounded transition"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmHide(null)}
                                className="px-1.5 py-0.5 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[10px] font-semibold rounded transition"
                              >
                                No
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleMarkReviewed(brand, !isReviewed); }}
                                disabled={markingBrand === brand}
                                className={`disabled:opacity-50 transition ${isReviewed ? 'text-blue-500 hover:text-gray-400' : 'text-gray-300 hover:text-blue-500'}`}
                                title={isReviewed ? 'Mark as unreviewed' : 'Mark as reviewed'}
                              >
                                {markingBrand === brand ? <Loader2 size={11} className="animate-spin" /> : '✓'}
                              </button>
                              <button
                                onClick={() => setSelectedBrand(brand)}
                                className="flex items-center gap-1"
                              >
                                {brand}
                                {typeof brandEstimates[brand] === 'number' && (
                                  <span className="text-[10px] font-bold opacity-70">({brandEstimates[brand]}% EUA)</span>
                                )}
                                {isEuaStale(brandEstimateUpdatedAt[brand]) && (
                                  <span
                                    onClick={(e) => { e.stopPropagation(); setSelectedBrand(brand); }}
                                    title={`This estimate is ${euaAgeDays(brandEstimateUpdatedAt[brand])} days old. Click to refresh.`}
                                    className="text-amber-500"
                                  >
                                    🔄
                                  </span>
                                )}
                                <ChevronRight size={10} className="opacity-60 group-hover:opacity-100 transition" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmHide(brand); }}
                                disabled={hidingBrand === brand}
                                className="ml-0.5 text-red-300 hover:text-red-500 disabled:opacity-50 transition"
                                title="Hide brand"
                              >
                                {hidingBrand === brand ? <Loader2 size={11} className="animate-spin" /> : <EyeOff size={11} />}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <div className="px-4 py-2 border-t border-gray-50 text-[10px] text-gray-400">
                Showing {filtered.length} of {visibleBrands.length} brand{filtered.length !== 1 ? 's' : ''} · click ✓ to mark reviewed · click a brand to see its products
                {visibleBrandCount < filtered.length ? ' · scroll to load more' : ''}
              </div>
            )}
          </div>
        )}
      </div>

      {showCrawler && (
        isUsMarketplaceUser(user)
          ? <UngateCrawlerV2 onClose={() => setShowCrawler(false)} />
          : <UngateCrawler onClose={() => setShowCrawler(false)} categories={[...new Set(Object.values(brandCategories).filter(Boolean))].sort()} />
      )}

      {showHiddenModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowHiddenModal(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-800 text-base">Hidden Brands</h2>
              <button onClick={() => setShowHiddenModal(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-500 transition">
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {hiddenBrands.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-10">No hidden brands.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {hiddenBrands.map(brand => (
                    <div key={brand} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full border border-gray-200">
                      <span>{brand}</span>
                      <button
                        onClick={() => handleRestore(brand)}
                        disabled={hidingBrand === brand}
                        className="ml-1 text-emerald-500 hover:text-emerald-700 disabled:opacity-50 transition text-[10px] font-bold"
                      >
                        {hidingBrand === brand ? <Loader2 size={9} className="animate-spin" /> : '↩ Restore'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showFillConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={e => { if (e.target === e.currentTarget) setShowFillConfirm(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-800 text-base">Fill All Estimated Ungated Asins</h2>
            </div>
            <div className="px-6 py-4 text-sm text-gray-600">
              <p>Skip brands that already have an EUA % calculated?</p>
              <p className="text-xs text-gray-400 mt-2">
                Brands with no estimate yet are always checked either way. This only affects brands
                that already have a value, regardless of how old it is.
              </p>
            </div>
            <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={() => setShowFillConfirm(false)}
                className="px-4 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowFillConfirm(false); fillAllEstimates(false); }}
                className="px-4 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
              >
                Check All Brands
              </button>
              <button
                onClick={() => { setShowFillConfirm(false); fillAllEstimates(true); }}
                className="px-4 py-1.5 text-xs font-semibold bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition"
              >
                Skip Existing EUAs
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkReview && (
        <BulkReviewModal
          onClose={() => setShowBulkReview(false)}
          onSubmit={handleBulkReview}
        />
      )}

      {showHistoricalLeads && (
        <HistoricalLeadsModal onClose={() => setShowHistoricalLeads(false)} />
      )}

      {showBulkKeepa && (
        <BulkKeepaModal
          brands={filtered}
          reviewedSet={reviewedSet}
          onMarkReviewed={handleBulkReview}
          brandEstimates={brandEstimates}
          onClose={() => setShowBulkKeepa(false)}
        />
      )}
      {selectedBrand && (
        <BrandProductsModal
          brand={selectedBrand}
          onClose={() => setSelectedBrand(null)}
          initialEstimatePct={typeof brandEstimates[selectedBrand] === 'number' ? brandEstimates[selectedBrand] : null}
          onEstimateUpdate={(b, pct, updatedAt) => {
            setBrandEstimates(prev => ({ ...prev, [b]: pct }));
            setBrandEstimateUpdatedAt(prev => ({ ...prev, [b]: updatedAt }));
          }}
        />
      )}
    </>
  );
};

export default UngatedBrandsWidget;